import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calculator, AlertTriangle, CheckCircle, AlertCircle, XCircle } from 'lucide-react';
import { getCalculatorById } from '../../data/calculators';
import type { CalculatorResult } from '../../data/calculators';
import AIReport from '../../components/AIReport';

const riskColors = { low: 'bg-green-100 text-green-900 border-green-400', moderate: 'bg-yellow-100 text-yellow-900 border-yellow-400', high: 'bg-orange-100 text-orange-900 border-orange-400', critical: 'bg-red-100 text-red-900 border-red-400' };
const riskIcons = { low: CheckCircle, moderate: AlertCircle, high: AlertTriangle, critical: XCircle };

export default function CalculatorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const calculator = id ? getCalculatorById(id) : undefined;
  const [values, setValues] = useState<Record<string, string | number>>({});
  const [result, setResult] = useState<CalculatorResult | null>(null);

  useEffect(() => {
    if (calculator) {
      const initialValues: Record<string, string | number> = {};
      calculator.inputs.forEach(input => {
        if (input.type === 'select' && input.options) initialValues[input.id] = input.options[0].value;
        else initialValues[input.id] = '';
      });
      setValues(initialValues);
    }
  }, [calculator]);

  if (!calculator) {
    return <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100 flex items-center justify-center"><div className="text-center bg-white p-8 rounded-xl shadow-lg"><h1 className="text-2xl font-bold text-gray-900 mb-4">Calculator Not Found</h1><Link to="/calculators" className="text-blue-600 hover:underline font-medium">Back to Calculators</Link></div></div>;
  }

  const handleInputChange = (inputId: string, value: string | number) => { setValues(prev => ({ ...prev, [inputId]: value })); setResult(null); };
  const handleCalculate = () => { setResult(calculator.calculate(values)); };
  const handleReset = () => {
    const initialValues: Record<string, string | number> = {};
    calculator.inputs.forEach(input => { if (input.type === 'select' && input.options) initialValues[input.id] = input.options[0].value; else initialValues[input.id] = ''; });
    setValues(initialValues);
    setResult(null);
  };

  const isFormValid = calculator.inputs.every(input => input.type === 'select' ? true : values[input.id] !== '' && values[input.id] !== undefined);
  const RiskIcon = result ? riskIcons[result.riskLevel] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-blue-100">
      <header className="bg-white/80 backdrop-blur-sm border-b border-blue-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/calculators" className="text-gray-600 hover:text-gray-900"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-lg shadow-md"><Calculator className="w-5 h-5 text-white" /></div>
              <div><h1 className="text-xl font-bold text-gray-900">{calculator.shortName}</h1><p className="text-sm text-gray-600">{calculator.name}</p></div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-2">Patient Parameters</h2>
            <p className="text-sm text-gray-700 mb-6">{calculator.description}</p>
            <div className="space-y-5">
              {calculator.inputs.map(input => (
                <div key={input.id}>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">{input.label}{input.unit && <span className="text-gray-500 font-normal ml-1">({input.unit})</span>}</label>
                  {input.type === 'select' ? (
                    <select value={values[input.id] ?? ''} onChange={(e) => handleInputChange(input.id, e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 font-medium bg-white">
                      {input.options?.map(option => <option key={String(option.value)} value={option.value}>{option.label}</option>)}
                    </select>
                  ) : (
                    <input type="number" value={values[input.id] ?? ''} onChange={(e) => handleInputChange(input.id, e.target.value)} min={input.min} max={input.max} step={input.step || 1} placeholder={input.placeholder} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 font-medium placeholder-gray-400 bg-white" />
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={handleCalculate} disabled={!isFormValid} className="flex-1 py-3 px-4 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors shadow-md">Calculate</button>
              <button onClick={handleReset} className="py-3 px-6 bg-gray-100 text-gray-800 font-bold rounded-lg hover:bg-gray-200 transition-colors border border-gray-200">Reset</button>
            </div>
          </div>

          <div>
            {result ? (
              <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Results</h2>
                <div className="text-center p-6 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl mb-4 shadow-md"><p className="text-sm text-blue-100 mb-2 font-medium">Score</p><p className="text-5xl font-bold text-white">{result.score}</p></div>
                <div className={`p-4 rounded-lg border-2 ${riskColors[result.riskLevel]} mb-4`}>
                  <div className="flex items-center gap-2">{RiskIcon && <RiskIcon className="w-6 h-6" />}<span className="font-bold text-lg capitalize">{result.riskLevel} Risk</span></div>
                  <p className="mt-2 font-medium">{result.interpretation}</p>
                </div>
                {Object.keys(result.details).length > 0 && (
                  <div className="mt-4"><h3 className="text-sm font-bold text-gray-800 mb-2">Score Breakdown</h3>
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200"><div className="grid grid-cols-2 gap-3 text-sm">
                      {Object.entries(result.details).map(([key, value]) => <div key={key} className="flex justify-between"><span className="text-gray-700 capitalize font-medium">{key.replace(/([A-Z])/g, ' $1').trim()}:</span><span className="font-bold text-gray-900">{value}</span></div>)}
                    </div></div>
                  </div>
                )}
                <AIReport calculatorId={calculator.id} calculatorName={calculator.name} calculatorShortName={calculator.shortName} inputs={values} result={result} />
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-lg p-6 flex items-center justify-center h-full min-h-[300px] border border-gray-100"><div className="text-center text-gray-500"><Calculator className="w-12 h-12 mx-auto mb-3 opacity-40" /><p className="font-medium">Enter parameters and click Calculate</p></div></div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
